import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = authed
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("axn_cart") || "[]"); } catch { return []; }
  });
  // Guards for the cart<->server sync: `hydratedRef` becomes true after we've
  // pulled the server cart once for this session (so subsequent local mutations
  // are safe to push back). `syncEnabledRef` prevents the push-back loop from
  // firing until the hydrate step completes.
  const hydratedRef = useRef(false);
  const syncEnabledRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if the browser just returned from Emergent Google Auth
    // (`#session_id=…` in the URL), skip the /auth/me probe. The AuthCallback
    // route will POST /auth/google/exchange first, set our JWT cookie, then
    // call refresh() itself. Racing /auth/me here would 401.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    refresh();
  }, [refresh]);

  // On authentication, hydrate the cart from the server (server always wins per
  // product spec). If the server has nothing but we have local items, upload
  // them so the next session sees them.
  useEffect(() => {
    if (!user || !user.id) {
      hydratedRef.current = false;
      syncEnabledRef.current = false;
      // Guest: run a stock scrub on the local cart so out-of-stock items
      // are dropped even before the buyer signs in. Debounced so it only
      // hits the endpoint once per user + cart shape change.
      const local = (() => {
        try { return JSON.parse(localStorage.getItem("axn_cart") || "[]"); }
        catch { return []; }
      })();
      if (user === false && local.length > 0) {
        api.post("/cart/scrub", { items: local }).then(({ data }) => {
          const kept = data?.items || [];
          const dropped = data?.dropped_items || [];
          if (dropped.length > 0) {
            setCart(kept);
            toast.error(`Removed ${dropped.length} item${dropped.length > 1 ? "s" : ""} — out of stock`);
          }
        }).catch(() => {});
      }
      return;
    }
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    (async () => {
      try {
        const { data } = await api.get("/account/cart");
        const serverItems = Array.isArray(data?.items) ? data.items : [];
        const dropped = Array.isArray(data?.dropped_items) ? data.dropped_items : [];
        if (serverItems.length > 0) {
          setCart(serverItems);
        } else {
          const local = JSON.parse(localStorage.getItem("axn_cart") || "[]");
          if (local.length > 0) {
            // Sync local → server (server will scrub OOS items on save).
            const { data: saved } = await api.post("/cart/scrub", { items: local });
            const keptLocal = saved?.items || local;
            const droppedLocal = saved?.dropped_items || [];
            if (droppedLocal.length > 0) {
              setCart(keptLocal);
              toast.error(`Removed ${droppedLocal.length} item${droppedLocal.length > 1 ? "s" : ""} — out of stock`);
            }
            await api.put("/account/cart", { items: keptLocal });
          }
        }
        if (dropped.length > 0) {
          toast.error(`Removed ${dropped.length} item${dropped.length > 1 ? "s" : ""} from your cart — out of stock`);
        }
      } catch { /* offline / not-authed — leave local cart alone */ }
      finally { syncEnabledRef.current = true; }
    })();
  }, [user]);

  useEffect(() => {
    localStorage.setItem("axn_cart", JSON.stringify(cart));
    // Debounced push to server whenever local cart changes for an authed user.
    if (!user || !user.id || !syncEnabledRef.current) return;
    const handle = setTimeout(() => {
      api.put("/account/cart", { items: cart }).catch(() => {});
    }, 400);
    return () => clearTimeout(handle);
  }, [cart, user]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("axn_token", data.access_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) localStorage.setItem("axn_token", data.access_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("axn_token");
    setUser(false);
  };

  // Cart helpers — item shape: { sku, name, image, variant, price, quantity }
  const addToCart = (item) => {
    setCart((c) => {
      const key = `${item.sku}::${item.variant}`;
      const existing = c.find((i) => `${i.sku}::${i.variant}` === key);
      if (existing) {
        return c.map((i) =>
          `${i.sku}::${i.variant}` === key ? { ...i, quantity: i.quantity + (item.quantity || 1) } : i
        );
      }
      return [...c, { ...item, quantity: item.quantity || 1 }];
    });
  };
  const removeFromCart = (sku, variant) =>
    setCart((c) => c.filter((i) => !(i.sku === sku && i.variant === variant)));
  const updateQty = (sku, variant, qty) =>
    setCart((c) => c.map((i) => (i.sku === sku && i.variant === variant ? { ...i, quantity: Math.max(1, qty) } : i)));
  const clearCart = () => setCart([]);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh, cart, addToCart, removeFromCart, updateQty, clearCart }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
